/**
 * Threads on the agent for any surface with a lane (D238–D242): a dashboard, the explorer, a form
 * draft, the general assistant. Five verbs over the same transports the Skills package uses; saas-api
 * relays them and carries the tenant's identity. `context` is what the page has in front of the
 * person, sent with the message for the lane to read.
 *
 * This replaces Ask Bono, which had its own store, its own drawer and its own page. The lane is the
 * same everywhere now, so a conversation started on a dashboard is the same kind of thing as one
 * started from the header.
 */
import type { BlockEvent, DecisionResult, ThreadPort, ThreadSummary } from "@bono-packages/chat";
import { skillsHttp, skillsSse, skillsApiBase } from "@/plugins/skillsPorts";

const enc = encodeURIComponent;

function portAt(base: () => string): ThreadPort {
  return {
    list: () => skillsHttp.get<ThreadSummary[]>(base()),
    create: (title) => skillsHttp.post<ThreadSummary>(base(), title ? { title } : {}),
    replay: (conversationId) =>
      skillsHttp.get<BlockEvent[]>(`${base()}/${enc(conversationId)}/replay`),
    send: async (conversationId, text, onEvent, signal, context) => {
      if (!skillsSse) throw new Error("This app has no stream to the agent.");
      await skillsSse(
        {
          path: `${base()}/${enc(conversationId)}/turns`,
          method: "POST",
          body: context === undefined ? { message: text } : { message: text, context },
        },
        (event) => onEvent(event as unknown as BlockEvent),
        signal,
      );
    },
    decide: (conversationId, proposalId, decision) =>
      skillsHttp.post<DecisionResult>(
        `${base()}/${enc(conversationId)}/proposals/${enc(proposalId)}`,
        { decision },
      ),
  };
}

/** Any surface: `surfaceThread('general', companyId)`, `surfaceThread('matrix', facilityId)`. */
export function surfaceThread(kind: string, id: string): ThreadPort {
  return portAt(() => `${skillsApiBase()}/surfaces/${enc(kind)}/${enc(id)}/conversations`);
}

/** A dashboard's thread has its own route, kept from the first lane (D239). */
export function dashboardThread(dashboardId: string): ThreadPort {
  return portAt(() => `${skillsApiBase()}/dashboards/${enc(dashboardId)}/conversations`);
}
